import { useState, useEffect } from 'react';
import { components } from '../api-abcscale';
import { apiGet, apiPost, apiPostForm, apiPutForm, ApiCallbacks } from '../apiClient';
import { resolveStorageUrl } from '../storageClient';

type Schemas = components['schemas'];

// Union of all category-specific spec shapes
export type ProductSpecs =
  | Schemas['FeaturesAgricultural']
  | Schemas['FeaturesBeltScales']
  | Schemas['FeaturesBenchScales']
  | Schemas['FeaturesCheckweighingScales']
  | Schemas['FeaturesCountingScales']
  | Schemas['FeaturesDynamometers']
  | Schemas['FeaturesFloorScales']
  | Schemas['FeaturesForceEquipment']
  | Schemas['FeaturesForkliftScales']
  | Schemas['FeaturesLoadCells']
  | Schemas['FeaturesMonorailScales']
  | Schemas['FeaturesPrinters']
  | Schemas['FeaturesRailwayScales']
  | Schemas['FeaturesTruckScales']
  | Schemas['FeaturesWeightIndicators'];

// Named convenience types — import these in product pages
export type AgriculturalSpecs    = Schemas['FeaturesAgricultural'];
export type BeltScalesSpecs      = Schemas['FeaturesBeltScales'];
export type BenchScalesSpecs     = Schemas['FeaturesBenchScales'];
export type CheckweighingSpecs   = Schemas['FeaturesCheckweighingScales'];
export type CountingScalesSpecs  = Schemas['FeaturesCountingScales'];
export type DynamometersSpecs    = Schemas['FeaturesDynamometers'];
export type FloorScalesSpecs     = Schemas['FeaturesFloorScales'];
export type ForceEquipmentSpecs  = Schemas['FeaturesForceEquipment'];
export type ForkliftScalesSpecs  = Schemas['FeaturesForkliftScales'];
export type LoadCellsSpecs       = Schemas['FeaturesLoadCells'];
export type MonorailScalesSpecs  = Schemas['FeaturesMonorailScales'];
export type PrintersSpecs        = Schemas['FeaturesPrinters'];
export type RailwayScalesSpecs   = Schemas['FeaturesRailwayScales'];
export type TruckScalesSpecs     = Schemas['FeaturesTruckScales'];
export type WeightIndicatorsSpecs = Schemas['FeaturesWeightIndicators'];

// ProductDto from api-abcscale.ts is the source type for this domain.
// Two divergences from the generated spec (portal API reality):
//   1. The API returns `category` (category name string), not `categoryId`.
//   2. The API returns `specs` as a parsed object (typed per category), not as a JSON string.
export type ProductDetail<TSpecs extends ProductSpecs = ProductSpecs> =
  Omit<Schemas['ProductDto'], 'categoryId' | 'specs'> & {
    category: string;
    specs: TSpecs;
  };

const PRODUCTS_BASE = `${process.env.REACT_APP_ADMIN_API_BASE}/products`;

function parseSpecs<TSpecs extends ProductSpecs>(p: any): ProductDetail<TSpecs> {
  const { categoryId, category, specs, ...rest } = p;
  return {
    ...rest,
    category: category ?? categoryId ?? '',
    specs: typeof specs === 'string' ? JSON.parse(specs) : (specs ?? {}),
  };
}

export async function getProduct<TSpecs extends ProductSpecs = ProductSpecs>(
  productId: string,
  callbacks: ApiCallbacks<ProductDetail<TSpecs>>
): Promise<void> {
  await apiGet(`${PRODUCTS_BASE}/${encodeURIComponent(productId)}`, {
    ...callbacks,
    onSuccess: (data: any) => callbacks.onSuccess(parseSpecs<TSpecs>(data)),
  }, true);
}

export async function getProductsByCategory<TSpecs extends ProductSpecs = ProductSpecs>(
  category: string,
  callbacks: ApiCallbacks<ProductDetail<TSpecs>[]>
): Promise<void> {
  await apiGet(`${PRODUCTS_BASE}?categoryId=${encodeURIComponent(category)}`, {
    ...callbacks,
    onSuccess: (data: any[]) => callbacks.onSuccess(data.map(p => parseSpecs<TSpecs>(p))),
  }, true);
}

export function useProduct<TSpecs extends ProductSpecs = ProductSpecs>(productId: string) {
  const [product, setProduct] = useState<ProductDetail<TSpecs> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setError(null);
    getProduct<TSpecs>(productId, {
      onSuccess: (data) => { setProduct(data); setLoading(false); },
      onNotFound: () => { setError('Producto no encontrado'); setLoading(false); },
      onError: (msg) => { setError(msg); setLoading(false); },
    });
  }, [productId]);

  return { product, loading, error };
}

export function useProductsByCategory<TSpecs extends ProductSpecs = ProductSpecs>(category: string) {
  const [products, setProducts] = useState<ProductDetail<TSpecs>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!category) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    getProductsByCategory<TSpecs>(category, {
      onSuccess: (data) => { setProducts(data); setLoading(false); },
      onError: (msg) => { setError(msg); setLoading(false); },
    });
  }, [category]);

  return { products, loading, error };
}

export function useProducts<TSpecs extends ProductSpecs = ProductSpecs>(
  category: string,
  editMode: boolean
): { products: ProductDetail<TSpecs>[]; loading: boolean; error: string | null; refresh: () => void } {
  const [products, setProducts] = useState<ProductDetail<TSpecs>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  const refresh = () => setRevision(r => r + 1);

  useEffect(() => {
    if (!category) { setLoading(false); return; }
    setLoading(true);
    setError(null);

    if (editMode) {
      getProductsByCategory<TSpecs>(category, {
        onSuccess: (data) => { setProducts(data); setLoading(false); },
        onError: (msg) => { setError(msg); setLoading(false); },
      });
    } else {
      fetch(resolveStorageUrl(`/data/products/products-${encodeURIComponent(category)}.json`), { cache: 'no-store' })
        .then(res => {
          if (!res.ok) { setProducts([]); setLoading(false); return; }
          return res.json();
        })
        .then((data: any[] | undefined) => {
          if (data !== undefined) {
            setProducts(data.map(p => parseSpecs<TSpecs>(p)));
            setLoading(false);
          }
        })
        .catch(() => { setProducts([]); setLoading(false); });
    }
  }, [category, editMode, revision]); // eslint-disable-line react-hooks/exhaustive-deps

  return { products, loading, error, refresh };
}

// Builds a multipart/form-data payload.
// `image` is always sent as empty string — the backend derives the filename from imageFile.
// When imageFile is omitted (edit with no new photo) the backend keeps the existing image.
function buildProductFormData(product: ProductDetail, imageFile?: File): FormData {
  const { category, ...rest } = product;
  const dto = JSON.stringify({ ...rest, categoryId: category, specs: JSON.stringify(product.specs), image: '' });
  const fd = new FormData();
  fd.append('dto', dto);
  if (imageFile) fd.append('imageFile', imageFile);
  return fd;
}

export async function updateProduct(
  product: ProductDetail,
  callbacks: ApiCallbacks<void>,
  imageFile?: File
): Promise<void> {
  const fd = buildProductFormData(product, imageFile);
  await apiPutForm(`${PRODUCTS_BASE}/${encodeURIComponent(product.productId ?? '')}`, fd, callbacks, true);
}

export async function createProduct(
  product: ProductDetail,
  callbacks: ApiCallbacks<ProductDetail>,
  imageFile?: File
): Promise<void> {
  const fd = buildProductFormData(product, imageFile);
  await apiPostForm(PRODUCTS_BASE, fd, {
    ...callbacks,
    onSuccess: (data: any) => callbacks.onSuccess(parseSpecs(data)),
  }, true);
}

export async function publishCategory(
  categoryId: string,
  callbacks: ApiCallbacks<void>
): Promise<void> {
  await apiPost(`${PRODUCTS_BASE}/${encodeURIComponent(categoryId)}/publish`, null, callbacks, true);
}

export async function publishAllCategories(callbacks: ApiCallbacks<void>): Promise<void> {
  await apiPost(`${PRODUCTS_BASE}/publish-categories`, null, callbacks, true);
}
